import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import PageHeader from '../../components/common/PageHeader';
import DynamicForm from '../../components/dynamic/DynamicForm';
import AlertToast from '../../components/common/AlertToast';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const INDUSTRY_FIELDS = [
  { fieldKey: 'name', label: 'Industry Name', fieldType: 'text', isRequired: true, group: 'Basic', width: 'col-md-6', order: 1 },
  { fieldKey: 'description', label: 'Description', fieldType: 'textarea', group: 'Basic', width: 'col-md-12', order: 2 },
  { fieldKey: 'icon', label: 'Bootstrap Icon Class', fieldType: 'text', group: 'Display', width: 'col-md-6', order: 3, placeholder: 'bi bi-building' },
  { fieldKey: 'color', label: 'Color', fieldType: 'color', group: 'Display', width: 'col-md-3', order: 4 },
  { fieldKey: 'order', label: 'Display Order', fieldType: 'number', group: 'Display', width: 'col-md-3', order: 5 },
];

export default function SAIndustries() {
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.get('/sa/industries').catch(() => ({ data: { data: [] } }));
    setIndustries(r.data.data || r.data.records || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (data) => {
    setSaving(true);
    try {
      if (editing) await api.put(`/sa/industries/${editing._id}`, data);
      else await api.post('/sa/industries', data);
      setToast({ show: true, message: `Industry ${editing ? 'updated' : 'created'}!`, type: 'success' });
      setShowForm(false); setEditing(null);
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Failed', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this industry?')) return;
    try {
      await api.delete(`/sa/industries/${id}`);
      setToast({ show: true, message: 'Industry deleted', type: 'success' });
      load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Cannot delete', type: 'danger' });
    }
  };

  return (
    <div>
      <PageHeader title="Industries" icon="bi-diagram-3"
        actions={<button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowForm(true); }}><i className="bi bi-plus-lg me-1"></i>Add Industry</button>}
      />

      {showForm && (
        <div className="card mb-4">
          <div className="card-header d-flex justify-content-between">
            <span>{editing ? 'Edit Industry' : 'New Industry'}</span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditing(null); }}></button>
          </div>
          <div className="card-body">
            <DynamicForm fields={INDUSTRY_FIELDS} initialData={editing || {}} onSubmit={handleSave} onCancel={() => { setShowForm(false); setEditing(null); }} loading={saving} submitLabel={editing ? 'Update' : 'Create'} />
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="row g-3">
          {industries.map(ind => (
            <div className="col-md-4" key={ind._id}>
              <div className="card h-100">
                <div className="card-body">
                  <div className="d-flex align-items-center gap-3 mb-2">
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${ind.color || '#0d6efd'}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: ind.color || '#0d6efd', fontSize: 18 }}>
                      <i className={ind.icon || 'bi bi-building'}></i>
                    </div>
                    <div>
                      <div className="fw-semibold">{ind.name}</div>
                      <div style={{ fontSize: 11, color: '#6c757d' }}>{ind.defaultModuleIds?.length || 0} modules</div>
                    </div>
                  </div>
                  <p className="text-muted mb-3" style={{ fontSize: 12 }}>{ind.description}</p>
                  <div className="d-flex gap-2">
                    <button className="btn btn-outline-primary btn-sm flex-fill" onClick={() => { setEditing(ind); setShowForm(true); }}><i className="bi bi-pencil me-1"></i>Edit</button>
                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(ind._id)}><i className="bi bi-trash"></i></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
